/**
 * Risk, MTM auto-squareoff, and intraday session rules for Paper Trading.
 *
 * AUTHORITATIVE RULES:
 * 1. Intraday (MIS) positions must square off same-day before session close (default 15:30 IST).
 * 2. Portfolio-level MTM kill switch: if todayMtm <= -lossLimit -> trigger MTM_LOSS_LIMIT.
 *    If todayMtm >= targetLimit -> trigger MTM_TARGET.
 * 3. Daily loss warning: fires once per day when realized loss exceeds DAILY_LOSS_LIMIT.
 */

export const INTRADAY_SQUAREOFF_HOUR = 15;
export const INTRADAY_SQUAREOFF_MIN = 30; // 3:30 PM IST

export type RiskAction = "MTM_LOSS_LIMIT" | "MTM_TARGET" | null;

/**
 * Checks if the current time in IST (Asia/Kolkata) is past the intraday MIS square-off cutoff.
 */
export function isPastIntradaySquareOff(
  nowDate: Date = new Date(),
  cutoffHour: number = INTRADAY_SQUAREOFF_HOUR,
  cutoffMin: number = INTRADAY_SQUAREOFF_MIN,
): boolean {
  try {
    const istStr = nowDate.toLocaleString("en-US", { timeZone: "Asia/Kolkata" });
    const ist = new Date(istStr);
    const h = ist.getHours();
    const m = ist.getMinutes();
    return h > cutoffHour || (h === cutoffHour && m >= cutoffMin);
  } catch {
    // Fallback if timezone conversion is unsupported in environment
    const utcHours = nowDate.getUTCHours();
    const utcMins = nowDate.getUTCMinutes();
    const istTotalMins = utcHours * 60 + utcMins + 330; // UTC+5:30
    const cutoffTotalMins = cutoffHour * 60 + cutoffMin;
    return (istTotalMins % 1440) >= cutoffTotalMins;
  }
}

/**
 * Evaluates whether portfolio-level MTM has hit configured loss limit or profit target.
 */
export function evaluateRiskAction(
  todayMtm: number,
  lossLimit: number,
  targetLimit?: number | null,
): RiskAction {
  if (lossLimit > 0 && todayMtm <= -lossLimit) {
    return "MTM_LOSS_LIMIT";
  }
  if (targetLimit != null && targetLimit > 0 && todayMtm >= targetLimit) {
    return "MTM_TARGET";
  }
  return null;
}

/**
 * Checks if daily realized loss threshold warning should fire.
 */
export function shouldWarnDailyLoss(
  todayRealizedLoss: number,
  dailyLossLimit: number,
  alreadyWarned: boolean,
): boolean {
  if (alreadyWarned) return false;
  return Math.abs(todayRealizedLoss) > dailyLossLimit;
}
